import { createRng } from './rng';
import { integrateLetters } from './engine';
import { createScoreState, decayStreak, registerHit, registerMiss } from './score';
import { createSpawnerState, tickSpawner } from './spawner';
import type {
  GameSettings,
  GameState,
  Letter,
  ScoreState,
} from './types';

export type GameEvent =
  | { type: 'spawn'; letter: Letter; interval: number; totalActive: number }
  | { type: 'miss'; letter: Letter; score: ScoreState }
  | {
      type: 'hit';
      letter: Letter;
      comboIncreased: boolean;
      score: ScoreState;
      achievements: string[];
    };

export interface GameInitOptions {
  seed?: number | string;
}

export function createGameState(
  settings: GameSettings,
  options: GameInitOptions = {}
): GameState {
  const rng = createRng(options.seed ?? Math.random() * Number.MAX_SAFE_INTEGER);
  const spawnState = createSpawnerState(settings.spawner);
  const score = createScoreState(settings.score);
  return {
    letters: [],
    spawnState,
    rng,
    clock: 0,
    nextLetterId: 1,
    score,
  };
}

function issueLetterId(state: GameState): string {
  const id = `letter-${state.nextLetterId}`;
  state.nextLetterId += 1;
  return id;
}

export function advanceGame(
  state: GameState,
  settings: GameSettings,
  dt: number
): GameEvent[] {
  const events: GameEvent[] = [];
  state.clock += dt;

  const spawnResult = tickSpawner(settings.spawner, state.spawnState, {
    dt,
    activeCount: state.letters.length,
    now: state.clock,
    rng: state.rng,
    issueId: () => issueLetterId(state),
  });

  if (spawnResult.letters.length > 0) {
    for (const letter of spawnResult.letters) {
      state.letters.push(letter);
      events.push({
        type: 'spawn',
        letter,
        interval: spawnResult.interval,
        totalActive: state.letters.length,
      });
    }
  }

  if (state.letters.length > 0) {
    const step = integrateLetters(state.letters, dt, settings.engine);
    state.letters = step.letters.filter((letter) => letter.alive);

    for (const missed of step.missed) {
      const miss = registerMiss(state.score, settings.score);
      state.score = miss.state;
      events.push({ type: 'miss', letter: missed, score: miss.state });
    }
  }

  state.score = decayStreak(state.score, settings.score, state.clock);

  return events;
}

export function resolveLetterHit(
  state: GameState,
  settings: GameSettings,
  letterId: string
): GameEvent | null {
  const index = state.letters.findIndex((letter) => letter.id === letterId && letter.alive);
  if (index === -1) {
    return null;
  }

  const [letter] = state.letters.splice(index, 1);
  const hit = registerHit(state.score, settings.score, state.clock);
  state.score = hit.state;

  const event: GameEvent = {
    type: 'hit',
    letter,
    comboIncreased: hit.comboIncreased,
    score: hit.state,
    achievements: hit.newlyUnlocked,
  };
  return event;
}

export function resetGame(state: GameState, settings: GameSettings): void {
  state.letters = [];
  state.spawnState = createSpawnerState(settings.spawner);
  state.score = createScoreState(settings.score);
  state.clock = 0;
  state.nextLetterId = 1;
}
